/**
 * パスワードのセルフリセット画面。
 *
 * 2 ステップで構成する:
 *   1. forgotPassword: email を入力 → Cognito がメールへリセットコードを送付。
 *   2. confirmForgotPassword: メールに届いたコード + 新パスワードを入力 → パスワードを再設定。
 *
 * 完了後はログイン画面 (`/login`) に戻り、新しいパスワードで改めてログインしてもらう。
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { APP_TITLE } from '../constants/appInfo';

import { useAuth } from './AuthProvider';
import { PasswordRequirements } from './PasswordRequirements';
import { PASSWORD_MIN_LENGTH, isPasswordValid } from './passwordPolicy';

type Step = 'request' | 'confirm';

export function ResetPasswordPage(): React.JSX.Element {
  const { forgotPassword, confirmForgotPassword, error: authError } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleRequest = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setLocalError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setStep('confirm');
      setInfo(`${email} 宛にパスワードリセットコードを送信しました。メールをご確認ください。`);
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setLocalError(null);
    setInfo(null);
    if (!isPasswordValid(password)) {
      setLocalError('パスワードが条件を満たしていません');
      return;
    }
    if (password !== passwordConfirm) {
      setLocalError('パスワードと確認用パスワードが一致しません');
      return;
    }
    setSubmitting(true);
    try {
      await confirmForgotPassword(email, code, password);
      // 完了後はログイン画面へ。
      navigate('/login', {
        replace: true,
        state: { resetEmail: email },
      });
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (submitting || !email) return;
    setLocalError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setInfo('パスワードリセットコードを再送しました。メールをご確認ください。');
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = localError ?? authError;

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>{APP_TITLE}</h1>
        <h2 className="login-subtitle">
          {step === 'request' ? 'パスワードのリセット' : '新しいパスワードの設定'}
        </h2>
        {step === 'request' ? renderRequestForm() : renderConfirmForm()}
      </div>
    </div>
  );

  function renderRequestForm(): React.JSX.Element {
    return (
      <form onSubmit={handleRequest}>
        <label>
          <span className="required">メールアドレス</span>
          <input
            type="email"
            data-testid="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
        </label>
        {info ? <div className="info-message">{info}</div> : null}
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        <button
          type="submit"
          data-testid="reset-request-submit"
          className="primary"
          disabled={submitting || !email}
        >
          {submitting ? '送信中...' : 'リセットコードを送信'}
        </button>
        <div className="login-footer">
          <Link to="/login">ログイン画面へ戻る</Link>
        </div>
      </form>
    );
  }

  function renderConfirmForm(): React.JSX.Element {
    return (
      <form onSubmit={handleConfirm}>
        <label>
          <span className="required">メールアドレス</span>
          <input
            type="email"
            data-testid="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
        </label>
        <label>
          <span className="required">確認コード</span>
          <input
            type="text"
            data-testid="confirmation-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={submitting}
          />
        </label>
        <label>
          <span className="required">新しいパスワード</span>
          <input
            type="password"
            data-testid="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={PASSWORD_MIN_LENGTH}
            disabled={submitting}
          />
        </label>
        <PasswordRequirements password={password} />
        <label>
          <span className="required">新しいパスワード (確認)</span>
          <input
            type="password"
            data-testid="password-confirm"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            minLength={PASSWORD_MIN_LENGTH}
            disabled={submitting}
          />
        </label>
        {info ? <div className="info-message">{info}</div> : null}
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        <button
          type="submit"
          data-testid="reset-confirm-submit"
          className="primary"
          disabled={submitting || !email || !code || !password || !passwordConfirm}
        >
          {submitting ? '設定中...' : 'パスワードを再設定'}
        </button>
        <div className="login-footer">
          <button
            type="button"
            data-testid="resend-code"
            onClick={handleResend}
            disabled={submitting || !email}
          >
            リセットコードを再送
          </button>
          <Link to="/login">ログイン画面へ戻る</Link>
        </div>
      </form>
    );
  }
}
